import React, { useState, useEffect } from "react";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import TokenFunctionSelect from "./TokenFunctionSelect";

import { ReactComponent as FunctionIcon } from "../../assets/images/function.svg";
import { ReactComponent as DateIcon } from "../../assets/images/token-date.svg";
import { ReactComponent as DealIcon } from "../../assets/images/token-deal.svg";
import { ReactComponent as ProductIcon } from "../../assets/images/icons8-product.svg";
import classNames from "../../utils/classnames";

export default function InsertTokenFunction({
  dealPropertyOptions,
  productPropertyOptions,
  functionOptions,
  dateTokenOptions,
  handleTokenSelect,
  handleFunctionSelect,
  isDropdownOpen,
  setIsDropdownOpen,
  hubspotPortal,
  isFocused,
}) {
  const [dealPropertyOptionsOpen, setDealPropertyOptionsOpen] = useState(false);
  const [productPropertyOptionsOpen, setProductPropertyOptionsOpen] =
    useState(false);
  const [functionOptionsOpen, setFunctionOptionsOpen] = useState(false);
  const [dateTokensOptionsOpen, setDateTokensOptionsOpen] = useState(false);

  const setAllClose = () => {
    setDealPropertyOptionsOpen(false);
    setProductPropertyOptionsOpen(false);
    setFunctionOptionsOpen(false);
    setDateTokensOptionsOpen(false);
  };

  const handleSelect = (e, setOptionsOpen) => {
    e.preventDefault();
    setAllClose();
    setOptionsOpen(true);
  };

  return (
    <Menu as="div" className="inline text-left">
      {({ open }) => {
        useEffect(() => {
          if (isDropdownOpen && !open) {
            setAllClose();
          }

          setIsDropdownOpen(open);
        }, [open]);

        return (
          <>
            <div className="absolute z-10 -translate-y-1/2 top-1/2 right-4">
              <Menu.Button
                className={classNames(
                  isDropdownOpen || isFocused
                    ? "text-primary"
                    : "text-tone-700",
                  "inline-flex w-full justify-center text-sm font-medium group/insert-button hover:text-primary ",
                )}
              >
                Insert
                <ChevronDownIcon
                  aria-hidden="true"
                  className={classNames(
                    isDropdownOpen || isFocused
                      ? "text-primary"
                      : "text-tone-700",
                    "-mr-1 w-5 h-5 group-hover/insert-button:text-primary",
                  )}
                />
              </Menu.Button>
            </div>

            <Menu.Items
              transition="true"
              className="!cursor-pointer border-tone-500 absolute -right-2 z-[9999] shadow-md mt-1 w-80 bg-white transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in rounded-lg  border"
            >
              <div className="absolute top-0 w-0 h-0 transform -translate-x-1/2 -translate-y-full border-b-8 border-l-8 border-r-8 right-3 border-l-transparent border-r-transparent border-b-tone-500"></div>
              <div className="absolute top-[1px] w-0 h-0 transform -translate-x-1/2 -translate-y-full border-b-8 border-l-8 border-r-8 right-3 border-l-transparent border-r-transparent border-b-white"></div>

              <div className="p-3">
                <Menu.Item
                  onClick={(e) => handleSelect(e, setDealPropertyOptionsOpen)}
                  onMouseEnter={() => setDealPropertyOptionsOpen(true)}
                  onMouseLeave={() => setDealPropertyOptionsOpen(false)}
                >
                  {({ close }) => (
                    <div className="relative flex p-4 rounded-lg group/deal gap-x-6 hover:bg-tone-100">
                      <div className="flex">
                        <DealIcon className="w-8 h-8 p-2 mt-1 mr-4 -ml-1 bg-orange-200 rounded-lg fill-orange-700" />
                        <div className="flex flex-col flex-1 gap-1">
                          <div className="text-sm font-semibold">
                            Deal Property
                          </div>
                          <p className="text-xs leading-tight text-tone-700">
                            Use properties from the deal that the template is
                            running on.
                          </p>
                        </div>
                        <ChevronRightIcon
                          aria-hidden="true"
                          className="self-center w-4 h-4 -mr-1 text-gray-400"
                        />
                      </div>
                      {dealPropertyOptionsOpen && (
                        <TokenFunctionSelect
                          options={dealPropertyOptions}
                          addPropertyLink={`https://${hubspotPortal.domain}/property-settings/${hubspotPortal.hub_portal}/properties?type=0-3`}
                          handleSelect={(option) => {
                            handleTokenSelect(option, "deal");
                            close();
                          }}
                          labelStyles={
                            "font-semibold  truncate whitespace-nowrap w-full"
                          }
                          descriptionStyles={
                            "font-mono truncate truncate whitespace-nowrap w-full"
                          }
                        />
                      )}
                    </div>
                  )}
                </Menu.Item>

                {productPropertyOptions && (
                  <>
                    <Menu.Item
                      onClick={(e) =>
                        handleSelect(e, setProductPropertyOptionsOpen)
                      }
                      onMouseEnter={() => setProductPropertyOptionsOpen(true)}
                      onMouseLeave={() => setProductPropertyOptionsOpen(false)}
                    >
                      {({ close }) => (
                        <div className="relative flex p-4 rounded-lg group/deal gap-x-6 hover:bg-tone-100">
                          <div className="flex">
                            <ProductIcon className="w-8 h-8 p-2 mt-1 mr-4 -ml-1 bg-indigo-200 rounded-lg fill-indigo-700" />
                            <div className="flex flex-col flex-1 gap-1">
                              <div className="text-sm font-semibold">
                                Product property
                              </div>
                              <p className="text-xs leading-tight text-tone-700">
                                Use properties from the product that this line
                                item is linked to.
                              </p>
                            </div>
                            <ChevronRightIcon
                              aria-hidden="true"
                              className="self-center w-4 h-4 -mr-1 text-gray-400"
                            />
                          </div>
                          {productPropertyOptions &&
                            productPropertyOptionsOpen && (
                              <TokenFunctionSelect
                                options={productPropertyOptions}
                                addPropertyLink={`https://${hubspotPortal?.domain}/property-settings/${hubspotPortal?.hub_portal}/properties?type=0-7`}
                                handleSelect={(option) => {
                                  handleTokenSelect(option, "product");
                                  close();
                                }}
                                labelStyles={
                                  "font-semibold  truncate whitespace-nowrap w-full"
                                }
                                descriptionStyles={
                                  "font-mono truncate truncate whitespace-nowrap w-full"
                                }
                              />
                            )}
                        </div>
                      )}
                    </Menu.Item>
                  </>
                )}

                <Menu.Item
                  onClick={(e) => handleSelect(e, setFunctionOptionsOpen)}
                  onMouseEnter={() => setFunctionOptionsOpen(true)}
                  onMouseLeave={() => setFunctionOptionsOpen(false)}
                >
                  {({ close }) => (
                    <div className="relative flex p-4 rounded-lg group/deal gap-x-6 hover:bg-tone-100">
                      <div className="flex">
                        <FunctionIcon className="w-8 h-8 p-2 mt-1 mr-4 -ml-1 bg-gray-200 rounded-lg fill-gray-700" />
                        <div className="flex flex-col flex-1 gap-1">
                          <div className="text-sm font-semibold">Function</div>
                          <p className="text-xs leading-tight text-tone-700">
                            Use advanced logic, calculate values and adjust
                            dates.
                          </p>
                        </div>
                        <ChevronRightIcon
                          aria-hidden="true"
                          className="self-center w-4 h-4 -mr-1 text-gray-400"
                        />
                      </div>
                      {functionOptionsOpen && (
                        <TokenFunctionSelect
                          options={functionOptions}
                          handleSelect={(option) => {
                            handleFunctionSelect(option);
                            close();
                          }}
                          hideSearch={true}
                          labelStyles={"font-mono font-bold"}
                        />
                      )}
                    </div>
                  )}
                </Menu.Item>

                <Menu.Item
                  onClick={(e) => handleSelect(e, setDateTokensOptionsOpen)}
                  onMouseEnter={() => setDateTokensOptionsOpen(true)}
                  onMouseLeave={() => setDateTokensOptionsOpen(false)}
                >
                  {({ close }) => (
                    <div className="relative flex p-4 rounded-lg group/deal gap-x-6 hover:bg-tone-100">
                      <div className="flex">
                        <DateIcon className="w-8 h-8 p-2 mt-1 mr-4 -ml-1 bg-teal-200 rounded-lg fill-teal-700" />
                        <div className="flex flex-col flex-1 gap-1">
                          <div className="text-sm font-semibold">
                            Date Token
                          </div>
                          <p className="text-xs leading-tight text-tone-700">
                            Helper tokens to fill out dates based on when the
                            template runs.
                          </p>
                        </div>
                        <ChevronRightIcon
                          aria-hidden="true"
                          className="self-center w-4 h-4 -mr-1 text-gray-400"
                        />
                      </div>
                      {dateTokensOptionsOpen && (
                        <TokenFunctionSelect
                          options={dateTokenOptions}
                          handleSelect={(option) => {
                            handleTokenSelect(option, "date");
                            close();
                          }}
                          hideSearch={true}
                          labelStyles={
                            "bg-teal-200 text-teal-700 rounded-full mx-0.5  px-3 py-1"
                          }
                        />
                      )}
                    </div>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </>
        );
      }}
    </Menu>
  );
}
